import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { ModalType } from '../../components/modals/constants';

export interface ModalEntry {
  id: string;
  modalType: ModalType;
  modalProps: Record<string, any>;
}

export type ModalState = ModalEntry[];

const initialState: ModalState = [];

/** Monotonic suffix so ids stay unique within a millisecond. */
let idCounter = 0;

const nextId = (modalType: ModalType): string =>
  `${modalType}-${Date.now()}-${idCounter++}`;

/**
 * Continuation actions are dispatched as raw objects (see
 * useRequestPayment), which bypasses the prepare callback that would have
 * assigned an id. The manager keys its Dialogs by id, so fill one in
 * rather than rendering with key={undefined}.
 */
const withId = (entry: ModalEntry): ModalEntry =>
  entry.id ? entry : { ...entry, id: nextId(entry.modalType) };

export const modalSlice = createSlice({
  name: 'modals',
  initialState,
  reducers: {
    pushModal: {
      reducer(state, action: PayloadAction<ModalEntry>) {
        state.push(withId(action.payload));
      },
      prepare({
        modalType,
        modalProps = {},
      }: {
        modalType: ModalType;
        modalProps?: Record<string, any>;
      }) {
        return {
          payload: {
            id: Date.now().toString(),
            modalType,
            modalProps,
          },
        };
      },
    },
    /**
     * Open a modal that is a destination rather than a step: any existing
     * entry of the same type is replaced instead of stacked (gdn-2957).
     *
     * The ledger drawer had this property deliberately — "calling this
     * twice doesn't stack, the second call replaces the props" — and the
     * surfaces that replaced it inherited the expectation. Without it,
     * every repeat trigger adds another dialog and the user has to dismiss
     * them one at a time.
     *
     * pushModal keeps stacking, which is right for modals that genuinely
     * layer (a confirm over a form).
     */
    replaceModal: {
      reducer(state, action: PayloadAction<ModalEntry>) {
        const remaining = state.filter(
          entry => entry.modalType !== action.payload.modalType,
        );
        remaining.push(withId(action.payload));
        return remaining;
      },
      prepare({
        modalType,
        modalProps = {},
      }: {
        modalType: ModalType;
        modalProps?: Record<string, any>;
      }) {
        return {
          payload: {
            id: nextId(modalType),
            modalType,
            modalProps,
          },
        };
      },
    },
    popModal(state) {
      state.pop();
    },
    clearModals() {
      return [];
    },
  },
});

export const { pushModal, replaceModal, popModal, clearModals } =
  modalSlice.actions;
export default modalSlice.reducer;
